// S4-4 — 알림 신청 폼 노출 대상 판정.
//
// 상세 페이지(P2)와 블로그 하단(P1)이 같은 판정을 쓴다. 중복 구현하지 않는다.
//
// lifecycle_stage 는 하루 한 번 자동 갱신되므로, 현장이 다음 단계로 넘어가면 폼이 자동으로 사라진다.
//
// ⚠️ 갱신 주체가 «바뀌었다»(2026-08-28). 이 주석이 오래 전임자를 가리키고 있었다:
//      (구) pg_cron `lifecycle-stage-refresh` → fn_refresh_lifecycle_stage()   — 2026-08-27 마지막 실행, 지금 active=false
//      (현) Vercel cron `/api/cron/stage-derive` 14:00 KST
//           → refresh_subscription_stages() → derive_subscription_stage()
//    ⛔ 옛 함수를 다시 켜지 말 것. 두 함수는 «기준이 다르다» — 켜면 서로 다른 답으로 stage 를 밀고 당긴다.
//    ⛔ 단계 판정을 읽을 때 옛 함수의 CASE 를 근거로 삼지 말 것. 실제로 그렇게 읽어 「137곳이 밀렸다」는
//       잘못된 진단이 나왔다(2026-09-20). 현행 기준으로 다시 재니 밀린 곳은 «0» 이었다.
//
// 단계 의미 (derive_subscription_stage 의 CASE 순서):
//   ⚠️ 발표 후 «30일» 은 award_announced 를 유지한다 — 계약이 끝났어도 그렇다. 의도된 설계다.
//   ⚠️ 계약 일정을 모르면 award_announced 에서 멈춘다. 입주일을 모르면 construction 까지만 말한다.
//      「모르는 것」과 「아닌 것」을 구분하는 원칙이 CASE 전체에 깔려 있다.
//
// (구) fn_refresh_lifecycle_stage 의 CASE — 참고용, 현행 아님:
//   post_move_in      입주월이 지남
//   move_in_started   입주월이 이번 달
//   move_in_ready     계약 체결 기간 종료 — "입주 준비"가 아니라 "계약 끝". 입주는 아직 미래다
//   contract_signing  계약 기간 중
//   award_announced   당첨자 발표됨
//   pre_announcement  접수 시작 전
//   subscription_open 접수 중
// 위 CASE 에 걸리지 않는 현장은 site_planning / unsold_active / landmark_active 등으로 남는다.

/** 알림 신청 폼을 노출할 생애주기 단계 */
import { kstYm } from '@/lib/cl-core/kst';

export const LEAD_ELIGIBLE_STAGES = [
  'site_planning',      // 분양예정
  'pre_announcement',   // 모집공고 전
  'subscription_open',  // 청약 접수중
  // H6-1 — 접수는 마감됐지만 발표 전이다. 관심 등록은 여전히 유효하다
  //   (예비·미계약 물량이 나온다). 접수중과 같은 취급.
  'award_pending',
  'award_announced',    // 당첨자 발표
  'unsold_active',      // 미분양·선착순·잔여세대
  // move_in_ready 는 이름과 달리 "계약 체결 기간 종료"일 뿐 입주는 아직 미래다.
  // 실측 720건 전부 입주예정 202609~203104 (과거 0건) — 입주월이 지나면 post_move_in 으로,
  // 이번 달이면 move_in_started 로 먼저 걸리기 때문이다. 분양가·일정 알림이 유효한 구간이라
  // 대상에 포함한다. P0 라이브 검증 현장(엄궁역 트라비스 하늘채)도 이 단계다.
  'move_in_ready',
  // v6-3: 입주월이 이번 달인 현장. 잔여 세대가 남아 있을 수 있어 상담이 유효하다.
  // 1,182 → 1,208 (+26). 그 외 단계는 건드리지 않는다 —
  // landmark_active 는 2단계에서 별도 문구(지역DB)로 다룬다.
  'move_in_started',

  // ── ONESHOT §C0 · 정비사업 단계 ──
  //
  // 이 6단계에 폼이 없어 **유입 1위 페이지가 리드를 못 받고 있었다.**
  // `울산 남구 달동 재개발`(조합설립)은 네이버 유입 48건에 CTA 완료 1건이 난 페이지인데
  // 폼이 없었다. 공고 전이라 분양가도 일정도 없지만 — **그래서** 사람들이 물어본다.
  //
  // ⚠️ 문구를 함께 갈라야 한다. 공고 전에 '분양 정보 안내' 는 어색하다.
  //    lib/apt/lead-copy.ts 가 단계별 문구를 낸다. 폼만 켜고 문구를 두면 전환이 안 난다.
  'union_established',    // 조합설립
  'constructor_selected', // 시공사 선정
  'plan_approved',        // 사업시행인가
  'mgmt_approved',        // 관리처분인가
  'construction',         // 착공
  'contract_signing',     // 계약
] as const;

// landmark_active 는 이름과 달리 분양 현장이 아니라 지역 대장 기축 아파트다(수완자이·명륜자이 등).
// 준공된 아파트에 "분양가·일정 알림 신청"을 붙이지 않는다 — 의도적으로 제외한다.

export function isLeadEligible(stage?: string | null): boolean {
  return !!stage && (LEAD_ELIGIBLE_STAGES as readonly string[]).includes(stage);
}

/* ── M5 §A-2 — 기축 매물·시세 상담 폼 ───────────────────────────────────
 *
 * ⚠️ isLeadEligible() 의 «반환값을 바꾸지 않는다». 블로그 하단(P1)·blog-safe-insert·
 *    hero-license 가 같은 판정을 쓴다. 바꾸면 기축 블로그 글에 분양 문구 폼이 붙고
 *    canUseHeroImage 의 광고 게이트 판정까지 함께 흔들린다.
 *    그래서 «새 함수를 하나 더» 둔다.
 *
 * 왜 필요한가: 부울경 폼 없는 662곳이 전부 기축이다. 광고 279곳은 기축 제외
 * 집합이라 광고비가 새고 있지는 않지만, 662곳은 검색으로 들어오는 페이지다.
 * `더샵 남양산센텀포레` 를 검색하는 사람은 매매·전세를 알아보는 사람이고 그것도 리드다.
 */

/** 준공돼 매매·전세가 도는 단계. 분양이 아니라 «매물» 을 묻는 자리다. */
export const RESALE_STAGES = ['post_move_in', 'landmark_active'] as const;

/**
 * ⚠️ 'region' 은 «단계에서 나오지 않는다». leadKind(stage) 는 지금까지처럼
 *    presale·resale·null 만 낸다 — 허브에는 lifecycle_stage 자체가 없기 때문이다
 *    (가리키는 현장이 하나가 아니다). 값을 «더하기만» 했고 기존 분기는 그대로다.
 *    허브 판정은 아래 regionLeadFormAvailable() 이 따로 낸다.
 */
export type LeadKind = 'presale' | 'resale' | 'region';

/**
 * 이 현장에 어떤 폼을 붙일지.
 *
 *   presale : LEAD_ELIGIBLE_STAGES 13단계 — 분양·진행상황
 *   resale  : post_move_in · landmark_active — 매물·시세
 *   null    : 그 외. 폼을 붙이지 않는다
 *
 * ⚠️ lifecycle_stage 가 비어 있으면 null 이다. 단계를 «추정해 채우지 않는다» (M5 §F-21).
 */
export function leadKind(stage?: string | null): LeadKind | null {
  if (!stage) return null;
  if (isLeadEligible(stage)) return 'presale';
  if ((RESALE_STAGES as readonly string[]).includes(stage)) return 'resale';
  return null;
}

/* ── 판정 ②′(ii) — 「기축이면서 재건축이 진행 중」 ─────────────────────────
 *
 * ⚠️ 이 현장들은 «폼이 없던» 게 아니다. post_move_in 이라 RESALE 로 잡혀
 *    「이 단지 매물·시세 상담」 폼이 붙어 있었다 — 틀린 폼이다.
 *    철거를 앞둔 단지에 매물을 묻게 하면 안 된다. 물어볼 것은 «진행 상황» 이다.
 *    (그래서 이 판정은 폼을 «켜는» 게 아니라 문구를 «가른다».)
 *
 * 판별식(MCPL-16 §2): 정비 기원 ∧ post_move_in ∧ 입주월이 과거로 확인되지 않음
 *                     ∧ complex_units 존재
 *   마지막 조건이 자기검증이다 — 재건축 «후» 계획 세대수를 안다는 것 자체가
 *   사업이 실제로 돌고 있다는 신호다(isPlannedUnits 계열).
 *   입주월이 과거면 그건 준공 «산물» 이라 매물 문구가 맞다(B 31곳, 제외).
 *
 * 실측(2026-09-20): 대상 23곳 · 누적 PV 570. 고현주공이 대표례다 —
 *   철거 전이라 주민이 살고 있어 stage 는 post_move_in 이 맞고, 그래서 이 판별이 필요하다.
 */
export function isRedevInProgress(site: {
  redev_origin?: boolean | null;
  lifecycle_stage?: string | null;
  complex_units?: number | null;
  move_in_date?: string | null;
}): boolean {
  if (!site.redev_origin) return false;
  if (site.lifecycle_stage !== 'post_move_in') return false;
  if (site.complex_units == null) return false;
  // 입주월이 «과거로 확인되면» 준공 산물이다. YYYYMM 형식만 신뢰한다.
  const m = String(site.move_in_date ?? '');
  if (/^\d{6}$/.test(m)) {
    const cur = kstYm().replace('-', ''); // KST 달력 'YYYYMM'
    if (m <= cur) return false;
  }
  return true;
}

/* ── V4-D P0-A — 「폼이 실제로 서는가」의 «단일 소스» ─────────────────────
 *
 * ⚠️ 이 사실을 «넷이 따로» 계산하고 있었고 그중 «둘이 틀렸다»(2026-08-30 실측):
 *     LeadForm      showLeadForm && site  + 내부 `if (!ENDPOINT) return null`   ✅
 *     하단 액션바   showLeadForm && !!ENDPOINT                                  ✅
 *     점프바 CTA    showLeadForm            ← ENDPOINT 를 «안 봤다»             ❌
 *     우측 레일     showLeadForm            ← 같음                              ❌
 *   그래서 ENDPOINT 가 비면 폼은 null 이 되어 DOM 에서 사라지는데 점프바·레일은
 *   그대로 남아 «사라진 #lead-form 을 가리키는 유령 앵커» 가 된다.
 *   눌러도 아무 일이 없다 — 하단 바의 침묵 반환과 «같은 병의 다른 표면» 이다.
 *
 * ⛔ 이 판정을 다시 손으로 조합하지 말 것. 폼 유무를 묻는 곳은 여기를 부른다.
 *   이름에 의미를 담았다 — 「폼이 붙을 자격이 있는가」(leadKind)가 아니라
 *   «실제로 화면에 서는가» 다. 자격이 있어도 엔드포인트가 없으면 서지 않는다.
 */
export const LEAD_ENDPOINT = process.env.NEXT_PUBLIC_LEAD_ENDPOINT || '';

export function leadFormAvailable(
  stage?: string | null,
  slug?: string | null,
): boolean {
  return !!slug && leadKind(stage) !== null && !!LEAD_ENDPOINT;
}

/* ── CPL v2 §4 레인 B-1 — 허브(지역) 리드 ────────────────────────────────
 *
 * ── 왜 필요했나 ──
 * docs/cpl/C1_기준선_20260923.md §1 실측: 최근 30일 리드 27건이 **100% 상세**
 * (`/apt/{slug}`) 착지다. 허브(`/apt/redev`·`region`·`area`) 착지 리드는 **0건**.
 * 그런데 `/apt/redev` 는 90일 부동산 1위 페이지(클릭 115 · CTR 13%)다.
 * 즉 허브가 리드를 «못 만드는» 게 아니라 **허브에 폼이 없었다**.
 *
 * ── 왜 leadKind(stage) 를 안 쓰나 ──
 * 허브에는 `lifecycle_stage` 가 «없다». 한 페이지가 수십~수백 구역을 가리키므로
 * 단계가 하나로 정해지지 않는다. 단계를 «추정해 채우지 않는다»(M5 §F-21)는 규칙이
 * 여기서도 그대로다 — 그래서 단계 판정을 우회하는 게 아니라 «축이 다른» 판정을 둔다.
 * ⛔ leadKind() 에 허브 분기를 끼워 넣지 말 것. 상세·블로그·blog-safe-insert·
 *    hero-license 가 같은 판정을 읽는다(§M5 §A-2 의 경고와 같은 계열).
 */

/**
 * 허브 리드의 `leads.site_slug` 접두.
 *
 * ⚠️ 실측(2026-09-23): `public.leads` 의 제약은 `leads_pkey`(PRIMARY KEY (id)) **하나뿐**이고
 *    `site_slug` 에 FK 도 CHECK 도 없다. `fn_insert_lead(p_site_slug text, …)` 도 text 다.
 *    그래서 `redev:부산` 같은 «현장이 아닌 식별자» 를 넣어도 적재가 거절되지 않는다.
 * ⚠️ 접두를 두는 이유는 집계다. `site_slug LIKE 'redev:%'` 한 줄로 허브 리드를 가르고,
 *    기존 27건(전부 실제 현장 slug)과 섞이지 않는다. 접두를 바꾸면 그 경계가 깨진다.
 */
export const REGION_LEAD_SLUG_PREFIX = 'redev:';

/** `부산` → `redev:부산`. 조립 규칙을 두 곳에 두지 않는다. */
export function regionLeadSlug(region: string): string {
  return `${REGION_LEAD_SLUG_PREFIX}${region}`;
}

/**
 * 허브에 폼이 «실제로 서는가».
 *
 * 지시서 R-3 기본값대로 **전 지역 노출**이다(부울경 한정 아님) — 지역으로 거르지 않는다.
 * 거르는 조건은 leadFormAvailable() 과 같은 둘뿐이다: 식별자가 있는가 · 엔드포인트가 있는가.
 * ⚠️ ENDPOINT 를 여기서 다시 읽지 않는다 — LEAD_ENDPOINT 한 곳이 원본이다(V4-D P0-A).
 */
export function regionLeadFormAvailable(region?: string | null): boolean {
  return !!region && !!LEAD_ENDPOINT;
}

/* ── CPL v4 §7 레인 B-2 — 지역 허브(`/apt/region`·`/apt/area`) 리드 ──────────
 *
 * B-1 과 «같은 진단의 다른 표면» 이다. 근거도 같다(최근 30일 리드 27건 100% 상세 착지,
 * 허브 착지 0건). 그래서 판정·문구·폼·계측을 «새로 만들지 않고» B-1 것을 그대로 탄다 —
 * 여기서 «더하는» 것은 식별자 두 개와, 그 식별자에서 착지 경로를 되찾는 함수 하나뿐이다.
 *
 * ⚠️ 노출 게이트는 regionLeadFormAvailable() 을 그대로 쓴다. 허브가 «빈 목록인가» 는
 *    표면마다 이미 판정기가 있으므로(region: countRegionRealSites, area: 프로필 10건 게이트)
 *    호출부가 그 결과와 AND 로 묶는다. ⛔ 여기에 세 번째 판정을 만들지 말 것.
 */

/** 시·도 허브(`/apt/region/{시도}`) 리드의 `leads.site_slug` 접두. */
export const REGION_HUB_LEAD_SLUG_PREFIX = 'region:';
/** 시군구 허브(`/apt/area/{시도}/{구}`) 리드의 `leads.site_slug` 접두. */
export const AREA_HUB_LEAD_SLUG_PREFIX = 'area:';

/* ── CPL v4 §7 레인 B-3 — 시군구 «미분양» 착지(`/apt/region/{시도}/{구}/unsold`) ──
 *
 * ⚠️ `area:` 를 재사용하지 않는다. 두 표면은 «착지가 다르다» — hubLeadPath 가 site_slug
 *    하나로 경로를 되찾는 구조라, 같은 접두를 쓰면 미분양 착지에서 난 리드의 시트 링크가
 *    `/apt/area/…` 로 찍혀 «담당자가 다른 페이지를 보게» 된다. 집계에서도 둘이 섞인다
 *    (`site_slug LIKE 'area:%'` 한 줄이 두 표면을 합쳐 버린다).
 * ⚠️ 구분자·모양은 `area:` 와 «동형» 이다(`{시도}/{구}`). 새 규약을 만든 것이 아니라
 *    표면 하나를 더한 것이다.
 * ⚠️ inquiry_type 은 여기서 정하지 않는다. lead-copy 의 leadCopyForHub() 가 `redev:` 가
 *    아닌 모든 허브를 region_hub 로 떨어뜨리고, 그 한 벌의 inquiryType 이 '분양상담' 이다.
 *    ⛔ 새 값을 만들지 말 것 — check_lead_unhandled_alert() 가 '분양상담' 정확일치만 본다.
 */
export const UNSOLD_HUB_LEAD_SLUG_PREFIX = 'unsold:';

/** `부산` → `region:부산`. */
export function regionHubLeadSlug(region: string): string {
  return `${REGION_HUB_LEAD_SLUG_PREFIX}${region}`;
}

/**
 * `부산`,`사상구` → `area:부산/사상구`.
 * ⚠️ 구분자가 `/` 인 것은 착지 경로(`/apt/area/{시도}/{구}`)와 «같은 모양» 이라서다 —
 *    hubLeadPath() 가 이 한 글자로 경로를 되찾는다. 시군구명에 `/` 는 없다.
 */
export function areaHubLeadSlug(region: string, sigungu: string): string {
  return `${AREA_HUB_LEAD_SLUG_PREFIX}${region}/${sigungu}`;
}

/** `부산`,`사상구` → `unsold:부산/사상구`. 구분자 규약은 areaHubLeadSlug 와 «같다». */
export function unsoldHubLeadSlug(region: string, sigungu: string): string {
  return `${UNSOLD_HUB_LEAD_SLUG_PREFIX}${region}/${sigungu}`;
}

/**
 * 허브 식별자 → 그 허브의 «착지 경로».
 *
 * ⚠️ 왜 필요한가: 시트에 남는 `siteUrl` 은 눌러서 열리는 링크여야 한다. 허브 식별자를
 *    현장 조립식(`/apt/{slug}`)에 그대로 태우면 `/apt/region%3A부산` 같은 죽은 링크가 난다
 *    (B-1 이 `redev:` 에서 이미 겪은 함정이다 — 그때는 regionName 으로 «따로» 조립했지만,
 *    표면이 셋이 되면 그 조립식도 셋이 된다. 접두가 이미 표면을 말하므로 여기 한 곳에서 낸다).
 * ⚠️ 한글이 들어간다. encodeURIComponent 를 빼먹지 말 것.
 *
 * 되찾을 수 없으면 빈 문자열이다 — 틀린 링크보다 빈 칸이 낫다.
 */
export function hubLeadPath(siteSlug?: string | null): string {
  const s = String(siteSlug ?? '');
  const i = s.indexOf(':');
  if (i < 0) return '';
  const prefix = s.slice(0, i + 1);
  const parts = s.slice(i + 1).split('/').filter(Boolean).map(encodeURIComponent);
  if (parts.length === 0) return '';
  if (prefix === REGION_LEAD_SLUG_PREFIX) return `/apt/redev/${parts[0]}`;
  if (prefix === REGION_HUB_LEAD_SLUG_PREFIX) return `/apt/region/${parts[0]}`;
  if (prefix === AREA_HUB_LEAD_SLUG_PREFIX && parts.length >= 2) return `/apt/area/${parts[0]}/${parts[1]}`;
  // B-3 — 미분양 착지. 카테고리 세그먼트가 «뒤» 에 붙는 것 말고는 area 와 같은 모양이다.
  if (prefix === UNSOLD_HUB_LEAD_SLUG_PREFIX && parts.length >= 2) return `/apt/region/${parts[0]}/${parts[1]}/unsold`;
  return '';
}
